import { ReactNode } from "react";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { AuthProvider } from "../helpers/useAuth";
import { ThemeModeProvider } from "../helpers/themeMode";
import { NumeroDetailSheetProvider } from "../helpers/useNumeroDetailSheet";
import { DateRangeProvider } from "../contexts/DateRangeContext";
import { OnboardingProvider } from "../contexts/OnboardingContext";
import { ComparatorCartProvider } from "../contexts/ComparatorCartContext";
import { TooltipProvider } from "./Tooltip";
import { SonnerToaster } from "./SonnerToaster";
import { ScrollToHashElement } from "./ScrollToHashElement";
// OnboardingOverlay/OnboardingTrigger retirés au profit de la seule
// OnboardingChecklist (Dashboard) : voir feat(onboarding) du 2026-05-12.
import { ComparatorCart } from "./ComparatorCart";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { OnboardingRouteTracker } from "./OnboardingRouteTracker";

/**
 * Gère les erreurs de manière cohérente et les reporte aux admins si nécessaire
 */
function handleGlobalError(error: unknown) {
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Erreur de parsing JSON (réponse HTML au lieu de JSON)
  if (
    errorMessage.includes("Unexpected token") ||
    errorMessage.includes("is not valid JSON") ||
    errorMessage.includes("JSON.parse")
  ) {
    toast.error("Erreur serveur", {
      description: "Un problème technique est survenu. L'équipe a été notifiée.",
      duration: 5000,
    });

    reportErrorToAdmin("json_parse_error", errorMessage);
    return;
  }

  // Erreur réseau
  if (errorMessage.includes("fetch") || errorMessage.includes("network") || errorMessage.includes("NetworkError")) {
    toast.error("Erreur de connexion", {
      description: "Impossible de contacter le serveur. Vérifiez votre connexion.",
      duration: 5000,
    });
    return;
  }
}

function reportErrorToAdmin(type: string, message: string) {
  try {
    fetch("/_api/error/report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type,
        url: "unknown",
        status: 0,
        message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
      }),
    }).catch(() => {});
  } catch {
    // Silently ignore
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleGlobalError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleGlobalError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute "fresh" window
      retry: (failureCount, error) => {
        // Ne pas réessayer les erreurs de parsing JSON
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (
          errorMessage.includes("Unexpected token") ||
          errorMessage.includes("is not valid JSON")
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

export const GlobalContextProviders = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeModeProvider>
          <DateRangeProvider>
            <OnboardingProvider>
              <ComparatorCartProvider>
                <NumeroDetailSheetProvider>
                  <ScrollToHashElement />
                  <ImpersonationBanner />
                  <OnboardingRouteTracker />
                  <TooltipProvider delayDuration={200}>
                    {children}
                    <SonnerToaster />
                    <ComparatorCart />
                  </TooltipProvider>
                </NumeroDetailSheetProvider>
              </ComparatorCartProvider>
            </OnboardingProvider>
          </DateRangeProvider>
        </ThemeModeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};
